import "../Events.js";
/** @namespace Data */
NS("Data");

/**
 * Simple Event Collection - An expanded version of standard JS Array, adding convenience methods for
 * manipulation. Add and remove changes to the Collection trigger a Collection
 * event.
 *
 * @class
 * @extends {Events}
 */
Data.EventCollection = Class(Events, /** @lends Data.EventCollection.prototype */ {
    /**
     * @type {Array.<T>}
     * @protected
     */
    items_: [],
    /**
     * @type {number}
     * @public
     */
    length: 0,
    /**
     * @param {Array.<T>} [items]
     */
    initialize(items) {
        this.items_ = items || [];
        this.update_();
    },
    /**
     * @fires Data.EventCollection#destroy
     * @returns {Data.EventCollection}
     */
    destroy() {
        this.clear();
        this.emit("destroy").off();
        return this;
    },
    /**
     * @param {Array.<t>|Data.EventCollection.<T>} [elems] If passed extends collection with `elems` and returns new extended array
     * @param {boolean} [replace=false] Extend current array of items or replace it
     * @returns {Array.<T>}
     */
    items(elems, replace) {
        if (elems != null) {
            if (replace) {
                this.items_ = elems.slice();
            } else {
                this.extend(elems);
            }
        }

        return this.items_;
    },
    /**
     * Slice collection items.
     *
     * @param {number} [start=0]
     * @param {number} [end]
     * @returns {Array.<T>}
     */
    slice(start = 0, end) {
        return this.items_.slice(start, end);
    },
    /**
     * @param {number} index
     * @param {*} [elem] If passed inserts this `elem` to `index` and returns it
     * @returns {T|undefined}
     */
    item(index, elem) {
        if (elem != null) {
            this.insertAt(index, elem);
        }
        return this.items_[index];
    },
    /**
     * @returns {Data.EventCollection}
     */
    clone() {
        return new Data.EventCollection(this.items_.slice());
    },
    /**
     * Clear items
     *
     * @return {Data.EventCollection}
     */
    clear() {
        this.items_ = [];
        this.update_();

        this.emit("clear");
        return this;
    },
    /**
     * @protected
     */
    update_() {
        this.length = this.items_.length;
    },
    /**
     * @param {function(value: T, key: number, collection: Data.EventCollection.<T>)} iterator
     * @param {*} [context=Data.EventCollection]
     * @returns {Data.EventCollection}
     * @throws InvalidArgumentError
     */
    forEach(iterator, context) {
        assert(Util.isFunction(iterator), "iterator not a function", InvalidArgumentError);

        for (var i = 0; i < this.length; ++i) {
            Util.call(iterator, context || this, [this.items_[i], i, this]);
        }

        return this;
    },
    /**
     * @param {function(value: T, key: number, collection: Data.EventCollection.<T>)} iterator
     * @param {*} [context=Data.EventCollection]
     * @returns {Array.<T>}
     * @throws InvalidArgumentError
     */
    map(iterator, context) {
        var result = [];

        assert(Util.isFunction(iterator), "iterator not a function", InvalidArgumentError);

        this.forEach((item, key) => result.push(Util.call(iterator, context || this, [item, key, this])));

        return result;
    },
    /**
     * @param {function(value: T, key: number, collection: Data.EventCollection.<T>)} iterator
     * @param {*} [context=Data.EventCollection]
     * @returns {Array.<T>}
     * @throws InvalidArgumentError
     */
    filter(iterator, context) {
        var result = [];

        assert(Util.isFunction(iterator), "iterator not a function", InvalidArgumentError);

        this.forEach((item, key) => {
            if (Util.call(iterator, context || this, [item, key, this])) {
                result.push(item);
            }
        });

        return result;
    },
    /**
     * @param {function(value: T, key: number, collection: Data.EventCollection.<T>)} iterator
     * @param {*} [context]
     * @returns {T|undefined}
     * @throws InvalidArgumentError
     */
    find(iterator, context) {
        assert(Util.isFunction(iterator), "iterator not a function", InvalidArgumentError);

        var result = this.filter(iterator, context);

        return result[0];
    },
    /**
     * @param {...T} elems
     * @returns {Data.EventCollection}
     */
    push(...elems) {
        for (var i in elems) {
            if (elems.hasOwnProperty(i)) {
                this.insertAt(this.length, elems[i]);
            }
        }

        return this;
    },
    /**
     * @param {Array.<T>|Data.EventCollection} elems
     * @returns {Data.EventCollection}
     */
    extend(elems) {
        return this.push.apply(this, elems.slice());
    },
    /**
     * @returns {T|undefined}
     */
    pop() {
        return this.removeAt(this.length - 1);
    },
    /**
     * @fires Data.EventCollection#insert
     * @param {number} index
     * @param {T} elem
     * @param {boolean} [truncate=false]
     * @returns {T|*}
     */
    insertAt(index, elem, truncate = false) {
        this.items_.splice(index, truncate ? this.length - index : 1, elem);
        this.update_();

        this.emit("insert", {
            elem, collection: this
        });

        return elem;
    },
    /**
     * @param {T} elem
     * @param {function(item: *,sample: *)} [iterator]
     * @param {*} [context]
     * @returns {number}
     */
    indexOf(elem, iterator, context) {
        var index = -1;

        this.items_.some((item, i) => {
            var eq;

            if (typeof iterator === 'function') {
                eq = Util.call(iterator, context || this, [item, elem]);
            } else {
                eq = elem == item;
            }

            return (index = eq ? i : -1, eq);
        });

        return index;
    },
    /**
     * @param {T} elem
     * @returns {boolean}
     */
    contains(elem) {
        return this.indexOf(elem) !== -1;
    },
    /**
     * Removes element from collection. Returns removed element or undefined.
     * @param {T} elem
     * @returns {T|undefined}
     */
    remove(elem) {
        var index;

        return (index = this.indexOf(elem), ~index ? this.removeAt(index) : undefined);
    },
    /**
     * Removes element from collection by index. Returns removed element or undefined
     * @fires Data.EventCollection#remove
     * @param {number} index
     * @returns {T|undefined}
     */
    removeAt(index) {
        var elem;

        if (this.items_.hasOwnProperty(index)) {
            elem = this.items_[index];
            this.items_.splice(index, 1);
            this.update_();

            this.emit("remove", {
                elem, collection: this
            });
        }

        return elem;
    }
});
